/**
 * smNodeCache — keep the identity of the node objects handed to ReactFlow
 * stable, rebuilding one only when something it displays actually changed.
 *
 * WHY THIS EXISTS (ENG-1072). ReactFlow's `adoptUserNodes` reuses a node's
 * internals — its measured size and its handle bounds — only while the object
 * identity it was given is unchanged:
 *
 *     if (checkEquality && userNode === internalNode?.internals.userNode) {
 *       nodeLookup.set(userNode.id, internalNode)      // internals survive
 *     } else {
 *       internalNode = { ...userNode, measured: { width: userNode.measured?.width, ... } }
 *     }
 *
 * A node with no measured size fails `isNodeInitialized`, so `getEdgePosition`
 * returns null and ReactFlow SILENTLY DROPS every edge touching it — no error,
 * no warning, just a state machine drawing its states with no transitions
 * between them. StateMachineEditor re-renders ten times a second (it polls the
 * runtime for the active state and clip timings), so handing ReactFlow a fresh
 * object for every node on every render made it discard and re-derive that
 * geometry continuously.
 *
 * To be exact about what is and is not established here: the `adoptUserNodes`
 * behaviour above is read straight from @xyflow/system, and the silent-drop
 * path is confirmed (ReactFlow's own `onError` never fires for it, because the
 * bail sits ahead of the error-008 branch). What has NOT been observed is this
 * churn actually costing a render in the app — the wires-missing state that
 * prompted this was traced to a hidden document, where ResizeObserver delivers
 * no callbacks at all and nothing gets measured regardless. So treat this as
 * removing a real hazard cheaply, not as the fix for a diagnosed outage.
 *
 * Cache per node id, and only mint a new object when the node itself, its
 * active flag, or its animation ratio changed.
 */

/**
 * Create a decorator with its own cache. One per editor instance — the cache is
 * keyed by node id, so two machines must not share one.
 *
 * The returned function takes the raw nodes plus:
 *   activeStateId  — id of the running state, or null
 *   clipTimings    — { [clipName]: { ratio } } from the debug poll
 *   getClipName    — (node) => clip name driving that state, or null
 *   makeOnChange   — (node) => handler for editing the node's fields
 *   makeOnDrillIn  — (node) => handler for opening it, or undefined
 */
export function createSmNodeCache() {
  const cache = new Map()

  return function decorateSmNodes(nodes, {
    activeStateId,
    clipTimings = {},
    getClipName = () => null,
    makeOnChange,
    makeOnDrillIn,
  }) {
    const live = new Set()

    const decorated = nodes.map(node => {
      live.add(node.id)
      const clipName  = getClipName(node)
      const isActive  = node.id === activeStateId
      const animRatio = clipName ? (clipTimings[clipName]?.ratio ?? null) : null

      const hit = cache.get(node.id)
      if (hit && hit.node === node && hit.isActive === isActive && hit.animRatio === animRatio) {
        return hit.out
      }

      const out = {
        ...node,
        data: {
          ...node.data,
          isActive,
          _debugAnimRatio: animRatio,
          onChange:  makeOnChange?.(node),
          onDrillIn: makeOnDrillIn?.(node),
        },
      }
      cache.set(node.id, { node, isActive, animRatio, out })
      return out
    })

    // Deleted nodes must not linger — the cache would otherwise grow for the
    // life of the editor.
    for (const id of [...cache.keys()]) {
      if (!live.has(id)) cache.delete(id)
    }

    return decorated
  }
}
